import { AnalyticsAPI } from '@core/api';
import { SupportedEntitlements } from '@core/models/entitlements';
import { FieldSeasonData } from '@core/models/seasons';
import { datadogLogs } from '@datadog/browser-logs';
import { CropSeason } from '@screens/YieldAnalysis/YieldAnalysis.models';
import { useBoundStore } from '@state/store';
import { useQuery } from '@tanstack/react-query';
import { uploadedCropSeasonAdapter } from '@utils/seasons.utils';
import { useProperty } from './queries/useProperty';
import { useEntitlements } from './useEntitlements';
import { useImageryFlags } from './useImageryFlags';

const utils = {
  fetchData: async (
    organizationId: string,
    farmId: string,
    updateSeasons: (uploadedSeasons: FieldSeasonData[]) => void
  ) => {
    try {
      const { data: response } = await AnalyticsAPI.get<CropSeason[]>(
        `/farm/${farmId}/crop-season`
      );

      const uploadedFieldSeasons = response.map((cropSeason) =>
        uploadedCropSeasonAdapter(cropSeason, organizationId)
      );

      updateSeasons(uploadedFieldSeasons);
      return uploadedFieldSeasons;
    } catch (error) {
      datadogLogs.logger.error('useFetchUploadedCropSeason: failed to load crop seasons', {
        error: error instanceof Error ? error.message : String(error),
        farm: farmId,
      });

      return [];
    }
  },
  getQueryKey: (orgId: string, farmId: string) => {
    return ['analyticsCropSeasonList', { orgId: orgId, farmId: farmId }];
  },
};
/**
 * Hook that fetches seasons from Analytics BFF Api
 * and adapt them to a field season data that Imagery Application can read and display in the list
 * @param organizationId ID of the Organization where seasons must be fetched
 */
export const useFetchUploadedCropSeason = (organizationId: string) => {
  const { property } = useProperty();
  const entitlements = useEntitlements();
  const { FlagShowYieldAnalysis } = useImageryFlags();
  const { updateSeasons } = useBoundStore((state) => state.seasons);
  const farmId = property?.id;

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- updateSeasons only writes the response into the store; it is not a request input.
  return useQuery({
    queryKey: utils.getQueryKey(organizationId, farmId!),
    queryFn: async () => {
      const data = await utils.fetchData(organizationId, farmId!, updateSeasons);
      return data;
    },
    enabled:
      (FlagShowYieldAnalysis || entitlements.has(SupportedEntitlements.YIELD_ANALYSIS)) &&
      Boolean(property?.id),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export default utils;
